import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Hidden } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Container,
  Drawer,
  Divider,
  List,
  ListSubheader,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@material-ui/core";
import {
  Dashboard as DashboardIcon,
  LocalShipping as LocalShippingIcon,
  ArrowBackIos,
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LocationOn as LocationOnIcon,
  Assessment as AssessmentIcon,
  VpnKey as VpnKeyIcon,
} from "@material-ui/icons";
import store from "store2";
import clsx from "clsx";
import { useSnackbar } from "notistack";

// Components
import Footer from "../Footer";
import MenuItem from "../MenuItem";
import OfflineComponent from "../OfflineComponent";

// helpers
import displayName from "../../helpers/displayName";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  appBar: {
    height: "10vh",
    backgroundColor: "#A3080C",
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },

  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
  drawerHeaderStart: {
    fontWeight: "bold",
    justifyContent: "flex-start",
    width: "80%",
    marginLeft: "10px",
  },
  drawerHeaderEnd: {
    justifyContent: "flex-end",
  },
  content: {
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // marginLeft: -drawerWidth,
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  back: {
    color: "#fff",
    fontSize: "small",
  },
  backLink: {
    color: "#fff",
    fontSize: "medium",
    fontWeight: "bold",
    textDecoration: "none",
  },
  toolbar: {
    margin: "0",
    top: "50%",
    transform: "translateY(-50%)",
  },
  title: {
    flexGrow: 1,
    fontWeight: "bold",
  },
  logoutButton: {
    marginLeft: "4px",
  },
  logoutIcon: {
    marginRight: "4px",
  },
  shippingButton: {
    border: "1px solid #fff",
    fontWeight: "bold",
  },
  shippingIcon: {
    marginRight: "4px",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

export default function withLayout(WrappedComponent, showBackLink = false) {
  const WithLayout = (props) => {
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const theme = useTheme();

    const [open, setOpen] = useState(false);

    const logout = () => {
      store.session.remove("token");
      store.session.remove("user");

      props.history.push("/login");
    };

    const handleDrawerOpen = () => {
      setOpen(true);
    };

    const handleDrawerClose = () => {
      setOpen(false);
    };

    React.useEffect(() => {
      const user = store.session.get("user");
      const channel =
        user.role?.id === 2 ? "deliveries" : `deliveries.${user.id}`;
      window.Echo.private(channel).listen(".DeliveryStatusUpdated", (event) => {
        if (event.message) {
          enqueueSnackbar(event.message, {
            autoHideDuration: 3000,
          });
        }
      });
    });

    const user = store.session.get("user");

    return (
      <>
        <OfflineComponent />
        <AppBar position="static" className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              className={clsx(classes.menuButton, open && classes.hide)}
            >
              <MenuIcon />
            </IconButton>

            {showBackLink && (
              <Link to="/" className={classes.backLink}>
                <ArrowBackIos className={classes.back} />
                {" Back"}
              </Link>
            )}

            {!showBackLink && (
              <>
                <Typography variant="h6" className={classes.title}>
                  KFC Delivery Tracker
                </Typography>
                <Button
                  color="inherit"
                  className={classes.shippingButton}
                  tag={Link}
                  href="/delivery/add"
                >
                  <Hidden only="xs">
                    <LocalShippingIcon className={classes.shippingIcon} />
                  </Hidden>
                  + Delivery
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>
        <Drawer
          className={classes.drawer}
          variant="persistent"
          anchor="left"
          open={open}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <div style={{ textAlign: "left" }}>
            <div className={classes.drawerHeader}>
              <div className={classes.drawerHeaderStart}>
                {displayName(user.firstName, user.lastName)}
              </div>
              <div className={classes.drawerHeaderEnd}>
                <IconButton onClick={handleDrawerClose}>
                  {theme.direction === "ltr" ? (
                    <ChevronLeftIcon />
                  ) : (
                    <ChevronRightIcon />
                  )}
                </IconButton>
              </div>
            </div>
          </div>
          <Divider />
          <List>
            <MenuItem
              iconComponent={DashboardIcon}
              label="Dashboard"
              url="/"
              location={props.location}
              access="view_dashboard"
            />
            <MenuItem
              iconComponent={PersonIcon}
              label="Riders"
              url="/riders"
              location={props.location}
              access="view_riders"
            />
            <MenuItem
              iconComponent={PersonAddIcon}
              label="Add Rider"
              url="/riders/add"
              location={props.location}
              access="add_rider"
            />
            <MenuItem
              iconComponent={LocationOnIcon}
              label="Rider Locations"
              url="/rider-locations"
              location={props.location}
              access="view_rider_locations"
            />
          </List>
          <Divider />
          <List
            component="nav"
            aria-labelledby="nested-list-subheader"
            subheader={
              <ListSubheader component="div" id="nested-list-subheader">
                Reports
              </ListSubheader>
            }
            className={classes.root}
          >
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Total Deliveries"
              url="/reports/total-deliveries"
              location={props.location}
              access="view_total_deliveries_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Average Delivery Time"
              url="/reports/average-delivery-time"
              location={props.location}
              access="view_average_delivery_time_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Average Outdoor Delivery Time"
              url="/reports/average-outdoor-delivery-time"
              location={props.location}
              access="view_average_outdoor_delivery_time_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Orders Outdoor Delivery Time"
              url="/reports/orders-outdoor-delivery-time"
              location={props.location}
              access="view_outdoor_delivery_time_per_order_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Orders At The Door Delivery Time"
              url="/reports/orders-at-the-door-delivery-time"
              location={props.location}
              access="view_at_the_door_delivery_time_per_order_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Average At The Door Time"
              url="/reports/average-at-the-door-time"
              location={props.location}
              access="view_average_at_the_door_time_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Deliveries Summary"
              url="/reports/deliveries-summary"
              location={props.location}
              access="view_deliveries_summary_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="Transactions"
              url="/reports/transactions"
              location={props.location}
              access="view_transactions_reports"
            />
            <MenuItem
              iconComponent={AssessmentIcon}
              label="FYF Tracker"
              url="/reports/fyf-tracker"
              location={props.location}
              access="view_fyf_tracker_reports"
            />
          </List>
          <Divider />
          {user.role.name === "admin" && (
            <List>
              <MenuItem
                iconComponent={VpnKeyIcon}
                label="Access Control"
                url="/access-control"
                location={props.location}
              />
            </List>
          )}
          <List>
            <ListItem button key="Logout" onClick={logout}>
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </Drawer>
        <main className={classes.content}>
          {/* <div className={classes.appBarSpacer} /> */}
          <Container maxWidth={false} className={classes.container}>
            <WrappedComponent {...props} />
          </Container>
        </main>
        <Footer />
      </>
    );
  };

  return WithLayout;
}
